import { CaretRightIcon, CheckIcon } from '@phosphor-icons/react';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useWindowDocument } from '../../../context/WindowDocumentContext';
import { Switch } from '../Switch/Switch';
import { Tooltip } from '../Tooltip/Tooltip';
import styles from './DropdownMenu.module.scss';

interface IDropdownMenuProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  openOnHover?: boolean;
  fullWidth?: boolean;
  /** A modal menu blocks clicks on the rest of the page while open. */
  modal?: boolean;
  avoidCollisions?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  tooltip?: string;
  contentClassName?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

interface IDropdownMenuItemProps {
  onSelect?: () => void;
  keepOpen?: boolean;
  icon?: ReactNode;
  variant?: 'default' | 'danger';
  /** Highlight this item (e.g. the current choice) with the accent color. */
  selected?: boolean;
  /** Grey background (`neutral`) or a leading check (`check`) instead of accent text for `selected`. */
  selectedTone?: 'accent' | 'neutral' | 'check';
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
  children: ReactNode;
}

interface IDropdownMenuCheckboxItemProps {
  checked: boolean;
  onSelect: () => void;
  icon?: ReactNode;
  indicatorPosition?: 'leading' | 'trailing';
  className?: string;
  children: ReactNode;
}

interface IDropdownMenuToggleItemProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

interface IDropdownMenuSubProps {
  children: ReactNode;
}

interface IDropdownMenuSubTriggerProps {
  icon?: ReactNode;
  disabled?: boolean;
  children: ReactNode;
}

interface IDropdownMenuSubContentProps {
  children: ReactNode;
  className?: string;
  sideOffset?: number;
  alignOffset?: number;
}

const DropdownMenuItem: FC<IDropdownMenuItemProps> = ({
  onSelect,
  keepOpen,
  icon,
  variant = 'default',
  selected = false,
  selectedTone = 'accent',
  disabled,
  onMouseEnter,
  onMouseLeave,
  className,
  children,
}) => {
  'use memo';

  return (
    <RadixDropdownMenu.Item
      className={classNames(
        styles.item,
        {
          [styles.itemDanger]: variant === 'danger',
          [styles.itemSelected]: selected && selectedTone === 'accent',
          [styles.itemSelectedNeutral]: selected && selectedTone === 'neutral',
        },
        className,
      )}
      onSelect={e => {
        if (keepOpen) e.preventDefault();
        onSelect?.();
      }}
      disabled={disabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {selectedTone === 'check' && (
        <span className={classNames(styles.itemIcon, styles.itemCheck)}>{selected && <CheckIcon weight="bold" />}</span>
      )}
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      {children}
    </RadixDropdownMenu.Item>
  );
};

const DropdownMenuCheckboxItem: FC<IDropdownMenuCheckboxItemProps> = ({
  checked,
  onSelect,
  icon,
  indicatorPosition = 'leading',
  className,
  children,
}) => {
  'use memo';

  const indicator = (
    <RadixDropdownMenu.ItemIndicator
      forceMount
      className={classNames(styles.itemIndicator, { [styles.itemIndicatorTrailing]: indicatorPosition === 'trailing' })}
    >
      <CheckIcon weight="bold" />
    </RadixDropdownMenu.ItemIndicator>
  );

  return (
    <RadixDropdownMenu.CheckboxItem
      className={classNames(styles.item, className)}
      checked={checked}
      onSelect={e => {
        e.preventDefault();
        onSelect();
      }}
    >
      {indicatorPosition === 'leading' && <span className={styles.itemIcon}>{indicator}</span>}
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      {children}
      {indicatorPosition === 'trailing' && indicator}
    </RadixDropdownMenu.CheckboxItem>
  );
};

const DropdownMenuToggleItem: FC<IDropdownMenuToggleItemProps> = ({ label, checked, onToggle, icon, disabled }) => {
  'use memo';

  return (
    <DropdownMenuItem keepOpen icon={icon} disabled={disabled} onSelect={onToggle}>
      <span className={styles.toggleItem}>
        {label}
        <Switch checked={checked} size="xs" />
      </span>
    </DropdownMenuItem>
  );
};

const DropdownMenuSeparator: FC = () => {
  'use memo';

  return <RadixDropdownMenu.Separator className={styles.separator} />;
};

const DropdownMenuLabel: FC<{ children: ReactNode }> = ({ children }) => {
  'use memo';

  return <RadixDropdownMenu.Label className={styles.label}>{children}</RadixDropdownMenu.Label>;
};

const DropdownMenuSub: FC<IDropdownMenuSubProps> = ({ children }) => {
  'use memo';

  return <RadixDropdownMenu.Sub>{children}</RadixDropdownMenu.Sub>;
};

const DropdownMenuSubTrigger: FC<IDropdownMenuSubTriggerProps> = ({ icon, disabled, children }) => {
  'use memo';

  return (
    <RadixDropdownMenu.SubTrigger className={styles.item} disabled={disabled}>
      {icon && <span className={styles.itemIcon}>{icon}</span>}
      <span className={styles.subTriggerLabel}>{children}</span>
      <CaretRightIcon size={14} weight="regular" className={styles.subTriggerChevron} />
    </RadixDropdownMenu.SubTrigger>
  );
};

const DropdownMenuSubContent: FC<IDropdownMenuSubContentProps> = ({
  children,
  className,
  sideOffset = 9,
  alignOffset = -4,
}) => {
  'use memo';

  // Not portalled: a body portal sits outside the modal scroll lock, which then swallows wheel events.
  return (
    <RadixDropdownMenu.SubContent
      className={classNames(styles.content, className)}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
    >
      {children}
    </RadixDropdownMenu.SubContent>
  );
};

const DropdownMenuRoot: FC<IDropdownMenuProps> = ({
  trigger,
  children,
  align = 'start',
  side = 'bottom',
  sideOffset = 4,
  openOnHover = false,
  fullWidth = false,
  modal = !openOnHover,
  avoidCollisions,
  open: controlledOpen,
  onOpenChange,
  tooltip,
  contentClassName,
  onCloseAutoFocus,
}) => {
  'use memo';

  const windowDoc = useWindowDocument();
  const [open, setOpen] = useState(false);
  const [isOpenThroughHover, setIsOpenThroughHover] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closedBy = useRef<'keyboard' | 'pick' | 'clickAway'>('keyboard');

  const cancelClose = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };

  const scheduleClose = () => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  };

  const handleTriggerMouseEnter = () => {
    cancelClose();
    setOpen(true);
    setIsOpenThroughHover(true);
  };

  const handleOpenChange = (v: boolean) => {
    if (isOpenThroughHover && !closeTimeout.current) return;

    if (openOnHover) {
      setOpen(v);
      if (!v) {
        setIsOpenThroughHover(false);
      }
    }

    onOpenChange?.(v);
  };

  const triggerEl = (
    <RadixDropdownMenu.Trigger
      asChild
      ref={triggerRef}
      className={styles.trigger}
      onMouseEnter={openOnHover ? handleTriggerMouseEnter : undefined}
      onMouseLeave={openOnHover ? scheduleClose : undefined}
    >
      {trigger}
    </RadixDropdownMenu.Trigger>
  );

  return (
    <RadixDropdownMenu.Root
      open={controlledOpen ?? (openOnHover ? open : undefined)}
      onOpenChange={handleOpenChange}
      modal={modal}
    >
      {tooltip === undefined ? triggerEl : <Tooltip title={tooltip}>{triggerEl}</Tooltip>}
      <RadixDropdownMenu.Portal container={windowDoc.body}>
        <RadixDropdownMenu.Content
          className={classNames(styles.content, { [styles.fullWidth]: fullWidth }, contentClassName)}
          align={align}
          side={side}
          sideOffset={sideOffset}
          avoidCollisions={avoidCollisions}
          onMouseEnter={openOnHover ? cancelClose : undefined}
          onMouseLeave={openOnHover ? scheduleClose : undefined}
          onPointerDownCapture={() => (closedBy.current = 'pick')}
          onPointerDownOutside={() => (closedBy.current = 'clickAway')}
          onKeyDownCapture={() => (closedBy.current = 'keyboard')}
          onCloseAutoFocus={(e: Event) => {
            if ((openOnHover && isOpenThroughHover) || tooltip) e.preventDefault();
            onCloseAutoFocus?.(e);
            const reason = closedBy.current;
            closedBy.current = 'keyboard';
            if (e.defaultPrevented || reason === 'keyboard') return;
            e.preventDefault();
            if (reason === 'pick') triggerRef.current?.focus({ focusVisible: false });
          }}
          onFocusOutside={openOnHover ? (e: Event) => isOpenThroughHover && e.preventDefault() : undefined}
        >
          {children}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
};

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Item: DropdownMenuItem,
  CheckboxItem: DropdownMenuCheckboxItem,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Sub: DropdownMenuSub,
  ToggleItem: DropdownMenuToggleItem,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
});
